import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  DELIVERY_CHARGE_MAX,
  DELIVERY_CHARGE_MIN,
} from '../shared/orderConstants';
import { formatAmount, toNumber } from '../shared/orderFormat';

/*
 * The manual delivery fee.
 *
 * The backend never sets this on its own — it only tells us what the standard
 * tier table would charge for the current subtotal (300+ free, 150+ → 15, else
 * 25). That number is offered as a one-click suggestion rather than applied,
 * because the whole point of a manual fee is that someone decided to override
 * the tiers, and silently reapplying them would undo that decision.
 *
 * The suggestion is hidden when it already matches the fee in place, so it only
 * appears when it would actually change something.
 */
const DeliveryChargeEditor = ({
  value,
  suggested,
  onSave,
  isPending,
  disabled,
}) => {
  const [draft, setDraft] = useState(() => formatAmount(value, { fallback: '0.00' }));
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(formatAmount(value, { fallback: '0.00' }));
    setError('');
  }, [value]);

  const commit = (raw) => {
    const parsed = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(parsed)) {
      setDraft(formatAmount(value, { fallback: '0.00' }));
      setError('');
      return;
    }
    if (parsed < DELIVERY_CHARGE_MIN || parsed > DELIVERY_CHARGE_MAX) {
      setError(`Between ${DELIVERY_CHARGE_MIN} and ${DELIVERY_CHARGE_MAX}.`);
      return;
    }
    setError('');
    if (parsed === (toNumber(value) ?? 0)) {
      setDraft(formatAmount(parsed, { fallback: '0.00' }));
      return;
    }
    onSave(parsed);
  };

  const suggestion = toNumber(suggested);
  const current = toNumber(value) ?? 0;
  const showSuggestion =
    !disabled && suggestion != null && suggestion !== current;

  if (disabled) {
    return (
      <span className="text-[13px] font-semibold text-[#3B4A54] tabular-nums">
        {current > 0 ? formatAmount(current) : 'Free'}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={draft}
          disabled={isPending}
          aria-label="Delivery charge"
          onChange={(event) =>
            setDraft(event.target.value.replace(/[^\d.]/g, ''))
          }
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              inputRef.current?.blur();
            }
            if (event.key === 'Escape') {
              setDraft(formatAmount(value, { fallback: '0.00' }));
              setError('');
              inputRef.current?.blur();
            }
          }}
          className="w-20 h-7 text-right px-2 rounded-md border border-[#D6DFE2] bg-white text-[13px] font-semibold text-nysmah-dark tabular-nums focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent disabled:opacity-50"
        />
        {isPending && (
          <Loader2
            className="w-3 h-3 animate-spin text-nysmah-muted"
            aria-hidden="true"
          />
        )}
      </span>

      {error ? (
        <span className="text-[10.5px] font-medium text-[#C2453D]">{error}</span>
      ) : showSuggestion ? (
        <button
          type="button"
          onClick={() => onSave(suggestion)}
          disabled={isPending}
          className="text-[10.5px] font-semibold text-nysmah-accent bg-transparent border-0 p-0 cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent rounded"
        >
          Standard rate is{' '}
          {suggestion === 0 ? 'free' : formatAmount(suggestion)} — apply
        </button>
      ) : null}
    </span>
  );
};

export default DeliveryChargeEditor;
